import { useEffect, useMemo, useState } from 'react'
import type { ManualLicenseCriteriaRequest, XeroInvoiceState } from '../../types'

export type CriteriaScenario =
  | 'org_with_invoice'
  | 'org_without_invoice'
  | 'invoice_without_org'
  | 'orphaned_invoices_available'
  | 'blocked'

export interface CriteriaFormState {
  formData: ManualLicenseCriteriaRequest
  setFormData: React.Dispatch<React.SetStateAction<ManualLicenseCriteriaRequest>>
  selectedFeatures: string[]
  setSelectedFeatures: React.Dispatch<React.SetStateAction<string[]>>
  selectedInvoiceId: string
  setSelectedInvoiceId: React.Dispatch<React.SetStateAction<string>>
  useCustomReference: boolean
  setUseCustomReference: React.Dispatch<React.SetStateAction<boolean>>
  isSubmitting: boolean
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  success: import('../../types').LicenseCriteria | null
  setSuccess: React.Dispatch<React.SetStateAction<import('../../types').LicenseCriteria | null>>
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  todayStr: string
  orphanedInvoices: XeroInvoiceState[]
  orgInvoices: XeroInvoiceState[]
  availableInvoices: XeroInvoiceState[]
  selectedInvoice: XeroInvoiceState | undefined
  hasOrgSelected: boolean
  hasOrphanedInvoices: boolean
  hasOrgInvoices: boolean
  hasSelectedInvoice: boolean
  isFormDisabled: boolean
  canSubmit: boolean
  scenario: CriteriaScenario
  handleInvoiceSelect: (invoiceId: string) => void
  resetForm: () => void
}

function deriveScenario(flags: {
  hasOrgSelected: boolean
  hasOrgInvoices: boolean
  hasSelectedInvoice: boolean
  hasOrphanedInvoices: boolean
}): CriteriaScenario {
  if (flags.hasOrgSelected && flags.hasOrgInvoices) return 'org_with_invoice'
  if (flags.hasOrgSelected && !flags.hasOrgInvoices) return 'org_without_invoice'
  if (!flags.hasOrgSelected && flags.hasSelectedInvoice) return 'invoice_without_org'
  if (!flags.hasOrgSelected && flags.hasOrphanedInvoices) return 'orphaned_invoices_available'
  return 'blocked'
}

function buildEmptyFormData(todayStr: string): ManualLicenseCriteriaRequest {
  return {
    organisationId: '',
    externalReference: '',
    seatsGranted: 5,
    volumeGrantedGb: 10,
    validFrom: todayStr,
    validUntil: '',
    accessTier: 'professional',
    featuresGranted: [],
  }
}

interface InvoiceFlags {
  orphanedInvoices: XeroInvoiceState[]
  orgInvoices: XeroInvoiceState[]
  availableInvoices: XeroInvoiceState[]
  selectedInvoice: XeroInvoiceState | undefined
  hasOrgSelected: boolean
  hasOrphanedInvoices: boolean
  hasOrgInvoices: boolean
  hasSelectedInvoice: boolean
}

function useInvoiceFlags(args: {
  organisationId: string
  selectedInvoiceId: string
  xeroInvoices: XeroInvoiceState[]
}): InvoiceFlags {
  const { organisationId, selectedInvoiceId, xeroInvoices } = args
  const orphanedInvoices = useMemo(
    () =>
      xeroInvoices.filter(
        (inv) =>
          inv.invoiceStatus === 'PAID' &&
          inv.paymentGatePasses &&
          !inv.entitlementId &&
          !inv.organisationId
      ),
    [xeroInvoices]
  )
  const orgInvoices = useMemo(() => {
    if (!organisationId) return []
    return xeroInvoices.filter(
      (inv) =>
        inv.organisationId === organisationId &&
        !inv.entitlementId &&
        inv.invoiceStatus === 'PAID' &&
        inv.paymentGatePasses
    )
  }, [organisationId, xeroInvoices])
  const availableInvoices = organisationId ? orgInvoices : orphanedInvoices
  const selectedInvoice = xeroInvoices.find((inv) => inv.id === selectedInvoiceId)
  return {
    orphanedInvoices,
    orgInvoices,
    availableInvoices,
    selectedInvoice,
    hasOrgSelected: !!organisationId,
    hasOrphanedInvoices: orphanedInvoices.length > 0,
    hasOrgInvoices: orgInvoices.length > 0,
    hasSelectedInvoice: !!selectedInvoiceId,
  }
}

function useInvoiceEffects(args: {
  organisationId: string
  selectedInvoiceId: string
  orgInvoices: XeroInvoiceState[]
  availableInvoices: XeroInvoiceState[]
  useCustomReference: boolean
  setSelectedInvoiceId: React.Dispatch<React.SetStateAction<string>>
  setFormData: React.Dispatch<React.SetStateAction<ManualLicenseCriteriaRequest>>
}): void {
  const {
    organisationId,
    selectedInvoiceId,
    orgInvoices,
    availableInvoices,
    useCustomReference,
    setSelectedInvoiceId,
    setFormData,
  } = args

  useEffect(() => {
    if (availableInvoices.length === 1 && !useCustomReference) {
      const invoice = availableInvoices[0]
      if (invoice) {
        setSelectedInvoiceId(invoice.id)
        setFormData((prev) => ({ ...prev, externalReference: invoice.xeroInvoiceNumber }))
      }
    }
  }, [availableInvoices, useCustomReference, setFormData, setSelectedInvoiceId])

  useEffect(() => {
    if (organisationId) {
      const isCurrentInvoiceForOrg = orgInvoices.some((inv) => inv.id === selectedInvoiceId)
      if (!isCurrentInvoiceForOrg) {
        setSelectedInvoiceId('')
        if (!useCustomReference) {
          setFormData((prev) => ({ ...prev, externalReference: '' }))
        }
      }
    }
  }, [
    organisationId,
    orgInvoices,
    selectedInvoiceId,
    useCustomReference,
    setFormData,
    setSelectedInvoiceId,
  ])
}

interface CriteriaFormStateBag {
  formData: ManualLicenseCriteriaRequest
  setFormData: React.Dispatch<React.SetStateAction<ManualLicenseCriteriaRequest>>
  selectedFeatures: string[]
  setSelectedFeatures: React.Dispatch<React.SetStateAction<string[]>>
  selectedInvoiceId: string
  setSelectedInvoiceId: React.Dispatch<React.SetStateAction<string>>
  useCustomReference: boolean
  setUseCustomReference: React.Dispatch<React.SetStateAction<boolean>>
  isSubmitting: boolean
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  success: import('../../types').LicenseCriteria | null
  setSuccess: React.Dispatch<React.SetStateAction<import('../../types').LicenseCriteria | null>>
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  todayStr: string
}

function useFormStateBag(): CriteriaFormStateBag {
  const todayStr = new Date().toISOString().split('T')[0] ?? ''
  const [formData, setFormData] = useState<ManualLicenseCriteriaRequest>(() =>
    buildEmptyFormData(todayStr)
  )
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([])
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const [useCustomReference, setUseCustomReference] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState<import('../../types').LicenseCriteria | null>(null)
  const [error, setError] = useState<string | null>(null)
  return {
    formData,
    setFormData,
    selectedFeatures,
    setSelectedFeatures,
    selectedInvoiceId,
    setSelectedInvoiceId,
    useCustomReference,
    setUseCustomReference,
    isSubmitting,
    setIsSubmitting,
    success,
    setSuccess,
    error,
    setError,
    todayStr,
  }
}

export function useLicenseCriteriaForm(args: {
  organisations: { id: string }[]
  xeroInvoices: XeroInvoiceState[]
}): CriteriaFormState {
  const { organisations, xeroInvoices } = args
  const bag = useFormStateBag()
  const flags = useInvoiceFlags({
    organisationId: bag.formData.organisationId,
    selectedInvoiceId: bag.selectedInvoiceId,
    xeroInvoices,
  })
  const isFormDisabled = organisations.length === 0 && !flags.hasOrphanedInvoices
  const canSubmit = flags.hasOrgSelected || flags.hasSelectedInvoice
  const scenario = useMemo(
    () =>
      deriveScenario({
        hasOrgSelected: flags.hasOrgSelected,
        hasOrgInvoices: flags.hasOrgInvoices,
        hasSelectedInvoice: flags.hasSelectedInvoice,
        hasOrphanedInvoices: flags.hasOrphanedInvoices,
      }),
    [
      flags.hasOrgSelected,
      flags.hasOrgInvoices,
      flags.hasSelectedInvoice,
      flags.hasOrphanedInvoices,
    ]
  )

  useInvoiceEffects({
    organisationId: bag.formData.organisationId,
    selectedInvoiceId: bag.selectedInvoiceId,
    orgInvoices: flags.orgInvoices,
    availableInvoices: flags.availableInvoices,
    useCustomReference: bag.useCustomReference,
    setSelectedInvoiceId: bag.setSelectedInvoiceId,
    setFormData: bag.setFormData,
  })

  const handleInvoiceSelect = (invoiceId: string): void => {
    bag.setSelectedInvoiceId(invoiceId)
    const invoice = xeroInvoices.find((inv) => inv.id === invoiceId)
    if (invoice) {
      bag.setFormData((prev) => ({ ...prev, externalReference: invoice.xeroInvoiceNumber }))
    }
  }

  const resetForm = (): void => {
    bag.setFormData(buildEmptyFormData(bag.todayStr))
    bag.setSelectedFeatures([])
    bag.setSelectedInvoiceId('')
    bag.setUseCustomReference(false)
  }

  return { ...bag, ...flags, isFormDisabled, canSubmit, scenario, handleInvoiceSelect, resetForm }
}

export function buildCriteriaRequest(args: {
  formData: ManualLicenseCriteriaRequest
  selectedInvoiceId: string
  selectedInvoice: XeroInvoiceState | undefined
  selectedFeatures: string[]
}): ManualLicenseCriteriaRequest {
  const { formData, selectedInvoiceId, selectedInvoice, selectedFeatures } = args
  const request: ManualLicenseCriteriaRequest = {
    organisationId: formData.organisationId || '',
    seatsGranted: formData.seatsGranted,
    validFrom: formData.validFrom,
    accessTier: formData.accessTier,
  }
  if (selectedInvoiceId) request.xeroInvoiceStateId = selectedInvoiceId
  if (selectedInvoice) {
    request.externalReference = selectedInvoice.xeroInvoiceNumber
  } else if (formData.externalReference) {
    request.externalReference = formData.externalReference
  }
  if (selectedFeatures.length > 0) request.featuresGranted = selectedFeatures
  if (formData.validUntil) request.validUntil = formData.validUntil
  if (formData.volumeGrantedGb) request.volumeGrantedGb = formData.volumeGrantedGb
  return request
}
